import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Shipment } from 'src/app/loads-shared/shipment-data/models/shipment';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { ShipmentsDataService } from '../../shared/shipments-data.service';
import { first, tap, catchError, finalize } from 'rxjs/operators';
import { COMMODITIES_HAULED, DEFAULT_ERROR_MESSAGE, STATUS_ASSIGNED, STATUS_CONFIRMATION_PENDING, STOPS } from '../../shared/constants';
import { ShipmentsUtilsService } from '../../shared/shipments-utils.service';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { SHIPMENTS_ROUTE, SHIPMENTS_ROUTE_MY_STATUS, SHIPMENTS_ROUTE_MY_SHIPMENTS } from '../../shared/routes';
import { ShipmentsStopsComponent } from '../../shipments-stops/shipments-stops.component';

const INSTRUCTION_KEYS = [
  'driverAssistedPickup',
  'forkLiftPickup',
  'liftgatePickup',
  'tarpPickup',
  'tieDownsPickup',
  'driverAssistedDelivery'
];
const INSTRUCTION_KEYS_MAP = {
  driverAssistedPickup: 'Driver Assisted Pickup',
  forkLiftPickup: 'Fork lift',
  liftgatePickup: 'Lift gate',
  tarpPickup: 'Tarp',
  tieDownsPickup: 'Tie-Downs',
  driverAssistedDelivery: 'Driver Assisted Delivery'
};
const SHIPMENT_TYPE_ONGOING = 'Ongoing';
const SHIPMENT_TYPE_UPCOMING = 'Upcoming';
@Component({
    selector: 'app-shipment-sub-detail',
    templateUrl: './shipment-sub-detail.component.html',
    styleUrls: ['./shipment-sub-detail.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      MatButtonModule,
      MatIconModule,
      MatCardModule,
      MatDividerModule,
      MatMenuModule,
      NgxSkeletonLoaderModule,
      ShipmentsStopsComponent
    ]
})
export class ShipmentSubDetailComponent implements OnInit {
  @Input() shipment: Shipment;
  pickupInstructionChips = [];
  deliveryInstructionChips = [];
  shipmentId: string;
  isContentReady: boolean = false;
  pickupFormattedDateTime: string | Date;
  deliveryFormattedDateAndTime: string | Date;
  shipmentType: string = '';
  loadTypeMap = {
    liveLoad: 'Live Load',
    hookAndDrop: 'Hook and Drop'
  };
  stops;
  pickupStop;
  deliveryAddress;
  pickupAddress;
  pageType: string = '';
  mainUnitNumber: string;
  mainVehicle: string;
  commodities = COMMODITIES_HAULED;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    private readonly toastService: ToastService,
    private readonly shipmentsDataService: ShipmentsDataService,
    private readonly shipmentsUtilsService: ShipmentsUtilsService,
    private readonly dateTimeService: DateTimeService,
    private readonly route: Router
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.pageType = params['pageType'];
    });
    if (!this.shipmentId && this.pageType !== 'history') {
      this.shipmentId = this.activatedRoute.snapshot.paramMap.get('id');
      this.getShipmentDetails();
    }
    const stateData = window.history.state;
    this.shipment = stateData?.shipment || null;
    if(this.shipment){
      this.isContentReady = true;
      this.getShipmentValues(this.shipment)
    }
  }

  navigateBack(): void {
    this.location.back();
  }

  navigateToDocuments(): void {
    let path = `${SHIPMENTS_ROUTE_MY_SHIPMENTS}/${this.shipment.id}/documents`;
    this.route.navigate([path]);
  }

  navigateToMyStatus(): void {
    let path = `/${SHIPMENTS_ROUTE}/${SHIPMENTS_ROUTE_MY_STATUS}/${this.shipment.id}`;
    this.route.navigate([path]);
  }

  getShipmentStatusClass(): string {
    return this.shipmentsUtilsService.getShipmentStatusClass(this.shipment.status);
  }

  getShipmentType(): void {
    if (this.shipment.status !== STATUS_CONFIRMATION_PENDING && this.shipment.status !== STATUS_ASSIGNED) {
      this.shipmentType = SHIPMENT_TYPE_ONGOING;
    } else {
      this.shipmentType = SHIPMENT_TYPE_UPCOMING;
    }
  }

  getShipmentDetails(): void {
      this.isContentReady = false;
      this.shipmentsDataService
        .getShipmentDetails(this.shipmentId)
        .pipe(
          first(),
          tap((response: Shipment) => {
            if (response) {
              this.shipment = response;
              this.getShipmentValues(this.shipment);
            }
          }),
          catchError(error => {
            this.toastService.showError(error?.message ?? DEFAULT_ERROR_MESSAGE);
            return error;
          }),
          finalize(() => {
            this.isContentReady = true;
          })
        )
        .subscribe();
  }

  getShipmentValues(shipment) {
    this.pickupInstructionChips = this.getChips(shipment?.pickup);
    this.deliveryInstructionChips = this.getChips(shipment?.delivery);
    this.pickupFormattedDateTime = shipment?.pickupFormattedDateTime;
    this.deliveryFormattedDateAndTime = shipment?.deliveryFormattedDateAndTime;
    this.getShipmentType();
    if (Array.isArray(shipment.delivery)) {
      this.stops = [...shipment.delivery];
      this.stops.sort((a, b) => a.order - b.order);
    } else {
      this.stops = [shipment.delivery];
    }
    if (shipment.pickup) {
      this.pickupStop = shipment.pickup;
      this.pickupStop.setOrder = 1;
      this.stops.unshift(this.pickupStop);
    }
    for (let i = 1; i < this.stops.length; i++) {
      this.stops[i].setOrder = i + 1;
    }
    const pickupStops = [STOPS.HOOK_TRAIKER, STOPS.PICK_LOAD];
    const pickups = this.stops.filter(el => pickupStops.includes(el.stopType));
    const dropStops = [STOPS.DELIVER_LOAD, STOPS.DROP_TRAILER];
    const dropOffs = this.stops.filter(el => dropStops.includes(el.stopType));
    this.pickupAddress = this.shipmentsUtilsService.getPickupAddressDateTime(pickups, shipment);
    this.deliveryAddress = this.shipmentsUtilsService.getDeliveryAddressDateTime(dropOffs, shipment);
    this.mainVehicle = this.getVehicleFromStops(this.stops)
    this.mainUnitNumber = this.getUnitNumber(this.mainVehicle)
  }

  getUnitNumber(vehicleId: string): string {
    const allVehicles = this.shipment?.vehicles || [];
    const vehicle = allVehicles.find(item => item.id === vehicleId);
    return vehicle?.unitNumber ?? '-';
  }

  getVehicleFromStops(stops: any[]): string {
    if (!stops || stops.length === 0) return null;

    const priorityOrder = [STOPS.PICK_LOAD, STOPS.HOOK_TRAIKER, STOPS.PICK_TRUCK];
  
    for (const stopType of priorityOrder) {
      const matchingStops = stops
        .filter(stop => stop.stopType === stopType && stop.vehicle)
        .sort((a, b) => a.setOrder - b.setOrder);
  
      if (matchingStops.length > 0) {
        return matchingStops[0].vehicle;
      }
    }
  
    return '-';
  }

  getCommoditiesName(value: string): string | null {
    const commodity = COMMODITIES_HAULED.find(item => item.value === value);
    return commodity ? commodity.name : value; 
  }  
  
  getChips(object: any): any[] {
    const chipArray = [];
    Object.keys(object).forEach(key => {
      if (INSTRUCTION_KEYS.includes(key) && object[key] !== false) {
        chipArray.push(INSTRUCTION_KEYS_MAP[key]);
      }
    });

    return chipArray;
  }
}
